import { APP_VERSION } from '../version'
import type { ApiMeta } from '../types'
import { refreshSessionExpiry } from './session'

export const API_URL = 'https://n8n.babaklabs.com/webhook/investment-course-api'
export const REQUEST_TIMEOUT_MS = 12_000

interface ApiSuccess<T> {
  ok: true
  data: T
  meta: ApiMeta
}

interface ApiFailure {
  ok: false
  error: {
    code: string
    message: string
    retryable?: boolean
    retryAfterSeconds?: number
    details?: Record<string, unknown>
  }
  meta?: Partial<ApiMeta>
}

export class ApiError extends Error {
  readonly code: string
  readonly retryable: boolean
  readonly retryAfterSeconds?: number
  readonly details?: Record<string, unknown>

  constructor(error: ApiFailure['error']) {
    super(error.message || 'The service could not complete the request.')
    this.name = 'ApiError'
    this.code = error.code || 'INTERNAL_ERROR'
    this.retryable = Boolean(error.retryable)
    this.retryAfterSeconds = error.retryAfterSeconds
    this.details = error.details
  }
}

export interface ApiOptions {
  token?: string | null
  timeoutMs?: number
  signal?: AbortSignal
  requestId?: string
}

export async function apiRequest<T>(
  action: string,
  payload: Record<string, unknown> = {},
  options: ApiOptions = {},
): Promise<{ data: T; meta: ApiMeta }> {
  const controller = new AbortController()
  const timeoutMs = options.timeoutMs ?? REQUEST_TIMEOUT_MS
  const timer = window.setTimeout(() => controller.abort(), timeoutMs)
  const requestId = options.requestId ?? crypto.randomUUID()
  const onAbort = () => controller.abort()
  options.signal?.addEventListener('abort', onAbort, { once: true })

  let response: Response
  try {
    response = await fetch(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action,
        requestId,
        appVersion: APP_VERSION,
        token: options.token ?? null,
        payload,
      }),
      signal: controller.signal,
    })
  } catch {
    if (controller.signal.aborted) {
      throw new ApiError({
        code: 'REQUEST_TIMEOUT',
        message: 'The service did not respond in time. Your course progress remains saved on this device.',
        retryable: true,
      })
    }
    throw new ApiError({
      code: 'NETWORK_ERROR',
      message: 'The service is unavailable. Your course progress remains saved on this device.',
      retryable: true,
    })
  } finally {
    window.clearTimeout(timer)
    options.signal?.removeEventListener('abort', onAbort)
  }

  let result: ApiSuccess<T> | ApiFailure
  try {
    result = await response.json() as ApiSuccess<T> | ApiFailure
  } catch {
    throw new ApiError({
      code: 'INVALID_RESPONSE',
      message: 'The service returned an unreadable response. Nothing was changed.',
      retryable: response.status >= 500,
    })
  }

  if (options.token && result.meta?.session?.expiresAt) {
    refreshSessionExpiry(options.token, result.meta.session.expiresAt)
  }

  if (!response.ok || !result.ok) {
    const failure = result as ApiFailure
    throw new ApiError(failure.error ?? {
      code: response.status === 401 ? 'UNAUTHORIZED' : 'INTERNAL_ERROR',
      message: 'The service could not complete the request.',
      retryable: response.status >= 500,
    })
  }

  if (!result.meta?.requestId || !result.meta.serverTime) {
    throw new ApiError({
      code: 'INVALID_RESPONSE',
      message: 'The service response was incomplete. Nothing was changed.',
      retryable: false,
    })
  }

  return result
}

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.retryAfterSeconds) return `${error.message} Try again in about ${error.retryAfterSeconds} seconds.`
    return error.message
  }
  return error instanceof Error ? error.message : 'The request could not be completed.'
}
